import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

/**
 * Shape de un item de moneda de país (country-coin) tal como
 * viene del backend.
 */
export interface CountryCoin {
  id: string;
  countryId: string;
  countryName: string;
  countryIso: string;
  coinId: string;
  coinCode: string;
  coinSymbol: string;
  coinName: string;
  typeCoinId: string;
  typeCoinName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Respuesta paginada del backend para country-coins.
 */
export interface CountryCoinPageResponse {
  items: CountryCoin[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Service para gestionar las monedas asociadas a un país.
 *
 * Endpoint principal:
 *   GET    /api/settings/countries/{countryId}/country-coins
 *   POST   /api/settings/countries/{countryId}/country-coins
 *   PUT    /api/settings/countries/{countryId}/country-coins/{id}
 *   DELETE /api/settings/countries/{countryId}/country-coins/{id}
 *
 * Paginación server-side con sort/direction/search/status.
 */
@Injectable({ providedIn: 'root' })
export class CountryCoinService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * LIST: Monedas de un país (server-side pagination)
   */
  getCountryCoins(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<CountryCoinPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    const url = `${this.apiUrl}/${countryId}/country-coins`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => ({
            id: it.id || '',
            countryId: it.countryId || '',
            countryName: it.countryName || '',
            countryIso: it.countryIso || '',
            coinId: it.coinId || '',
            coinCode: it.coinCode || '',
            coinSymbol: it.coinSymbol || '',
            coinName: it.coinName || '',
            typeCoinId: it.typeCoinId || '',
            typeCoinName: it.typeCoinName || '',
            status: this.normalizeStatus(it.status),
            createdAt: it.createdAt || '',
            updatedAt: it.updatedAt || '',
          })) as CountryCoin[],
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as CountryCoinPageResponse;
      }),
    );
  }

  /**
   * GET by id: detalle para Ver/Editar (edit response: id, coinId, typeCoinId, status)
   */
  getCountryCoinById(
    countryId: string,
    id: string,
  ): Observable<CountryCoin> {
    const url = `${this.apiUrl}/${countryId}/country-coins/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((response) => {
        const it = response.data;
        return {
          id: it.id || '',
          countryId: it.countryId || '',
          countryName: it.countryName || '',
          countryIso: it.countryIso || '',
          coinId: it.coinId || '',
          coinCode: it.coinCode || '',
          coinSymbol: it.coinSymbol || '',
          coinName: it.coinName || '',
          typeCoinId: it.typeCoinId || '',
          typeCoinName: it.typeCoinName || '',
          status: this.normalizeStatus(it.status),
          createdAt: it.createdAt || '',
          updatedAt: it.updatedAt || '',
        } as CountryCoin;
      }),
    );
  }

  /**
   * CREATE: Asociar una moneda a un país
   */
  createCountryCoin(
    countryId: string,
    payload: { coinId: string; typeCoinId: string },
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/country-coins`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(url, payload)
      .pipe(
        map((res) => ({
          message: res.message || 'Moneda asociada exitosamente',
        })),
      );
  }

  /**
   * UPDATE: Actualizar la asociación de moneda a un país
   */
  updateCountryCoin(
    countryId: string,
    id: string,
    payload: { coinId: string; typeCoinId: string; status?: string },
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/country-coins/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(url, payload)
      .pipe(
        map((res) => ({
          message: res.message || 'Moneda actualizada exitosamente',
        })),
      );
  }

  /**
   * DELETE: Eliminar la asociación de moneda de un país
   */
  deleteCountryCoin(
    countryId: string,
    id: string,
  ): Observable<{ message: string }> {
    const url = `${this.apiUrl}/${countryId}/country-coins/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Moneda eliminada exitosamente',
        })),
      );
  }
}
